import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

interface User {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
}

interface UserContextType {
    user: User | null;
    loading: boolean;
    logout: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            // Fetch user data when component mounts if token exists
            setLoading(true);
            api.get<{ data: User }>('/auth/user/')
                .then(response => {
                    setUser(response.data.data);
                    setLoading(false);
                })
                .catch(() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('refresh');
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, []);

    const logout = async () => {
        try {
            await api.post('/auth/logout/');
            localStorage.removeItem('token');
            localStorage.removeItem('refresh');
            setUser(null);
            window.location.href = '/';
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <UserContext.Provider value={{ user, loading, logout }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}; 